"use client";

import NextLink from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import { Avatar, Drawer, Dropdown, cn } from "@heroui/react";
import {
  ArrowSquareOut,
  BracketsAngle,
  CaretDown,
  GearSix,
  Lifebuoy,
  List,
  ShieldCheck,
  SquaresFour,
  SignOut,
  X,
} from "@phosphor-icons/react";

import { ButtonLink } from "@/components/ui/button-link";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { signOut } from "@/lib/auth-client";
import { AI_SUITE_UPSCALE_URL } from "@/lib/migration";

export interface NavUser {
  name: string;
  email: string;
  image: string | null;
}

/**
 * Pricing and Billing are gone from both lists: `lib/migration.ts` redirects
 * those routes to the migration notice, and a nav item that bounces you
 * somewhere else is worse than no nav item. What is left is what still does
 * something — the API reference for integrators winding down, support, and the
 * account pages that hold the data.
 */
const NAV_LINKS = [
  { href: "/api", label: "API", icon: BracketsAngle },
  { href: "/support", label: "Support", icon: Lifebuoy },
] as const;

const ACCOUNT_LINKS = [
  { href: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { href: "/dashboard/settings", label: "Settings", icon: GearSix },
  { href: "/dashboard/privacy", label: "Privacy", icon: ShieldCheck },
] as const;

/**
 * The interactive half of the marketing header.
 *
 * Split from `SiteHeader` so the session can be read on the server and handed
 * down: the alternative is fetching it in the browser, which shows every
 * signed-in visitor a "Log in" button for a moment on every page load.
 *
 * Signed in, the auth buttons are replaced by an account menu rather than
 * joined by one. Someone with a session has no use for "Get started", and two
 * competing calls to action is how a header stops being navigable.
 */
/**
 * Hides the bar on the way down, brings it back on the way up.
 *
 * Written straight to the node's style rather than held in React state: this
 * fires on every scroll event, and a `setState` there re-renders the whole
 * nav — links, dropdown, drawer — sixty times a second to move one element.
 * The `Loupe` takes the same approach for the same reason.
 *
 * Three details that separate this from the version that feels broken:
 *
 *  - `HIDE_BELOW` keeps the bar put through the first screenful, so it does
 *    not vanish the instant someone nudges the wheel in the hero.
 *  - `DELTA` swallows sub-pixel and rubber-band scrolling. Without it, a
 *    trackpad's momentum and iOS's bounce at the top flip the direction
 *    repeatedly and the bar strobes.
 *  - `focusin` brings it back when a keyboard user tabs into a link that is
 *    currently off-screen; otherwise focus lands somewhere invisible.
 */
function useHideOnScroll(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const HIDE_BELOW = 96;
    const DELTA = 6;

    let last = window.scrollY;
    let hidden = false;

    const apply = (next: boolean) => {
      if (next === hidden) return;
      hidden = next;
      el.style.transform = next ? "translateY(-100%)" : "translateY(0)";
    };

    const onScroll = () => {
      const y = window.scrollY;
      const moved = y - last;
      if (Math.abs(moved) < DELTA) return;
      last = y;
      apply(moved > 0 && y > HIDE_BELOW);
    };

    const reveal = () => apply(false);

    window.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("focusin", reveal);
    return () => {
      window.removeEventListener("scroll", onScroll);
      el.removeEventListener("focusin", reveal);
    };
  }, [ref]);
}

export function SiteNav({ user }: { user: NavUser | null }) {
  const pathname = usePathname();
  const router = useRouter();
  const header = useRef<HTMLElement>(null);

  useHideOnScroll(header);

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const initials =
    user?.name
      .split(" ")
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";

  async function handleSignOut() {
    await signOut();
    // `refresh` matters as much as `push`: without it the server components
    // keep rendering against the cached signed-in session.
    router.push("/");
    router.refresh();
  }

  return (
    /*
     * Fully transparent: no fill, no scrim, no blur, no border. The page
     * shows through it completely.
     *
     * What keeps the labels readable is the hide-on-scroll behaviour above
     * rather than a background — the bar is out of the way while the page is
     * moving down through photographs and code samples, and the one place it
     * is guaranteed to sit still is the top of a page, over the hero.
     *
     * Transform, not `display` or `top`: it stays off the layout path, so
     * hiding the bar never reflows the page beneath it.
     */
    <header
      ref={header}
      className="sticky top-0 z-50 transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none"
    >
      <nav
        aria-label="Main"
        className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-4 px-6"
      >
      <NextLink
        href="/"
        className="flex w-fit items-center gap-2 text-[15px] font-semibold tracking-wide uppercase"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/Blue.svg"
          alt=""
          width={22}
          height={22}
          className="object-contain"
          aria-hidden
        />
        UPSCALR
      </NextLink>

      {/* Links and actions share the right-hand side. The centred column was
          pinned to the middle of the page rather than sitting with the
          controls it belongs beside. */}
      <div className="flex items-center gap-1">
        <div className="mr-3 hidden items-center gap-6 md:flex">
          {NAV_LINKS.map((link) => (
            <NextLink
              key={link.href}
              href={link.href}
              aria-current={isActive(link.href) ? "page" : undefined}
              className={cn(
                // 44px tall inside a 56px bar: the label alone was a ~26px
                // target, under every platform's minimum.
                "relative flex min-h-11 items-center gap-1.5 text-sm transition-colors",
                isActive(link.href)
                  ? "text-foreground"
                  : "text-muted hover:text-foreground",
              )}
            >
              {/* Decorative: the label beside it names the destination. */}
              <link.icon
                weight="duotone"
                className="size-4 shrink-0"
                aria-hidden
              />
              {link.label}
              {isActive(link.href) ? (
                <span
                  className="bg-accent absolute bottom-2.5 left-0 h-px w-full"
                  aria-hidden
                />
              ) : null}
            </NextLink>
          ))}
        </div>

        <ThemeToggle />

        {user ? (
          <Dropdown>
            <Dropdown.Trigger
              aria-label="Account menu"
              className="hover:bg-surface-secondary flex items-center gap-1.5 rounded-md px-1.5 py-1 transition-colors"
            >
              <Avatar size="sm">
                {user.image ? <Avatar.Image src={user.image} alt="" /> : null}
                <Avatar.Fallback>{initials}</Avatar.Fallback>
              </Avatar>
              <CaretDown
                weight="bold"
                className="text-muted size-3"
                aria-hidden
              />
            </Dropdown.Trigger>

            <Dropdown.Popover className="min-w-56">
              <Dropdown.Menu>
                <Dropdown.Section>
                  <div className="flex flex-col gap-0.5 px-2 py-1.5">
                    <span className="truncate text-sm font-medium">
                      {user.name}
                    </span>
                    <span className="text-muted truncate text-xs">
                      {user.email}
                    </span>
                  </div>
                </Dropdown.Section>

                {ACCOUNT_LINKS.map((link) => (
                  <Dropdown.Item
                    key={link.href}
                    id={link.href}
                    onAction={() => router.push(link.href)}
                  >
                    <link.icon weight="duotone" className="size-[18px]" />
                    {link.label}
                  </Dropdown.Item>
                ))}

                <Dropdown.Item id="sign-out" onAction={handleSignOut}>
                  <SignOut weight="bold" className="size-[18px]" />
                  Sign out
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        ) : (
          <>
            <NextLink
              href="/login"
              className="text-muted hover:text-foreground hidden px-3 py-2 text-sm transition-colors md:inline-flex"
            >
              Log in
            </NextLink>
            <ButtonLink
              href={AI_SUITE_UPSCALE_URL}
              size="sm"
              className="hidden gap-1.5 md:inline-flex"
            >
              Open AI Suite
              <ArrowSquareOut weight="bold" className="size-3.5" aria-hidden />
            </ButtonLink>
          </>
        )}

        <Drawer>
          <Drawer.Trigger
            aria-label="Open menu"
            className="text-muted hover:text-foreground inline-flex size-9 items-center justify-center rounded-md md:hidden"
          >
            <List weight="bold" className="size-5" />
          </Drawer.Trigger>

          <Drawer.Content placement="right">
            <Drawer.Dialog className="w-72">
              <Drawer.Header className="flex items-center justify-between">
                <Drawer.Heading className="text-sm font-medium">
                  Menu
                </Drawer.Heading>
                <Drawer.CloseTrigger
                  aria-label="Close menu"
                  className="text-muted hover:text-foreground inline-flex size-8 items-center justify-center rounded-md"
                >
                  <X weight="bold" className="size-4" />
                </Drawer.CloseTrigger>
              </Drawer.Header>

              <Drawer.Body className="divide-default/40 flex flex-col divide-y">
                {user ? (
                  <div className="flex items-center gap-3 py-3.5">
                    <Avatar size="sm">
                      {user.image ? (
                        <Avatar.Image src={user.image} alt="" />
                      ) : null}
                      <Avatar.Fallback>{initials}</Avatar.Fallback>
                    </Avatar>
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">
                        {user.name}
                      </span>
                      <span className="text-muted truncate text-xs">
                        {user.email}
                      </span>
                    </div>
                  </div>
                ) : null}

                {[...NAV_LINKS, ...(user ? ACCOUNT_LINKS : [])].map((link) => (
                  <NextLink
                    key={link.href}
                    href={link.href}
                    aria-current={isActive(link.href) ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-2.5 py-3.5 text-sm transition-colors",
                      isActive(link.href)
                        ? "text-foreground font-medium"
                        : "text-muted hover:text-foreground",
                    )}
                  >
                    <link.icon
                      weight="duotone"
                      className="size-[18px]"
                      aria-hidden
                    />
                    {link.label}
                  </NextLink>
                ))}
              </Drawer.Body>

              <Drawer.Footer className="flex flex-col gap-2">
                {user ? (
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="border-default/60 text-muted hover:text-foreground flex w-full items-center justify-center gap-2 rounded-md border py-2 text-sm transition-colors"
                  >
                    <SignOut weight="bold" className="size-4" />
                    Sign out
                  </button>
                ) : (
                  <>
                    <ButtonLink href={AI_SUITE_UPSCALE_URL} fullWidth className="gap-1.5">
                      Open AI Suite
                      <ArrowSquareOut weight="bold" className="size-3.5" aria-hidden />
                    </ButtonLink>
                    <ButtonLink href="/login" variant="ghost" fullWidth>
                      Log in
                    </ButtonLink>
                  </>
                )}
              </Drawer.Footer>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer>
        </div>
      </nav>
    </header>
  );
}
